import {makeStyles} from "@material-ui/core"
import {ModernTheme} from "../../../theme/modern/tokens"
import {COARSE, touchTarget} from "../../../theme/modern/touchTargets"

/**
 * Ported from the prototype's `components/AppBar.css` (modern rule set only —
 * the `html[data-ui="classic"]` overrides live in the Classic header instead).
 */
export default makeStyles((theme: ModernTheme) => ({
    appbar: {
        position: "sticky",
        top: 0,
        zIndex: 20,
        height: theme.modern.appBarHeight,
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "0 18px",
        background: theme.modern.panel,
        borderBottom: `1px solid ${theme.modern.line}`,
        flex: "none",

        "& > *": {flex: "0 0 auto"},

        // phones: more controls than fit on a 390px row, so wrap instead of
        // overflowing the page
        "@media (max-width:640px)": {
            flexWrap: "wrap",
            height: "auto",
            minHeight: theme.modern.appBarHeight,
            padding: "8px 12px",
            gap: "8px 10px",
        },
    },
    spacer: {
        flex: "1 1 0",
        minWidth: 8,
        "@media (max-width:640px)": {display: "none"},
    },
    rowbreak: {
        display: "none",
        "@media (max-width:640px)": {
            "&&": {
                display: "block",
                flex: "0 0 100%",
                height: 0,
                order: 4,
            },
        },
    },

    brand: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 0,
        color: theme.modern.ink,
        "@media (max-width:640px)": {
            "&&": {order: 1, marginRight: "auto"},
        },
    },
    brandmark: {
        height: 20,
        width: "auto",
        display: "block",
        color: theme.modern.ink,
    },

    // Classic | Modern segmented toggle
    uimode: {
        display: "inline-flex",
        alignItems: "center",
        background: theme.modern.line2,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 8,
        padding: 2,
        gap: "2px",
        "@media (max-width:640px)": {
            "&&": {order: 7},
        },
    },
    uimode_btn: {
        font: `600 11.5px ${theme.modern.sans}`,
        color: theme.modern.ink2,
        background: "none",
        border: "none",
        padding: "4px 12px",
        borderRadius: 6,
        cursor: "pointer",
        transition: "background .15s, color .15s",
        whiteSpace: "nowrap",

        "&:hover": {color: theme.modern.ink},

        "@media (max-width:640px)": {padding: "4px 10px"},

        // after the 640 px rule on purpose: equal specificity, so source order
        // is what stops that smaller padding from winning on a phone
        [COARSE]: touchTarget({padding: "0 14px"}),
    },
    uimode_btn__active: {
        background: theme.modern.panel,
        color: theme.modern.accent,
        boxShadow: theme.modern.shadowSegment,

        "&:hover": {color: theme.modern.accent},
    },

    // icon buttons (moon, sign out)
    iconbtn: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        height: 30,
        padding: "0 10px",
        borderRadius: 7,
        border: `1px solid ${theme.modern.line}`,
        background: theme.modern.panel,
        color: theme.modern.ink2,
        fontFamily: theme.modern.sans,
        fontWeight: 500,
        fontSize: 12,
        whiteSpace: "nowrap",
        cursor: "pointer",

        "&:hover": {
            borderColor: theme.modern.ink3,
            color: theme.modern.ink,
        },

        "& svg": {width: 14, height: 14, flex: "none"},

        // this rule shrank the bar's controls to 28 px on exactly the devices
        // that can least afford it; the coarse rule below restores 44 px for
        // touch and leaves 28 px only for a narrow window on a desktop
        "@media (max-width:640px)": {height: 28, order: 3},

        [COARSE]: touchTarget({height: "auto", padding: "0 12px"}),
    },
    iconbtn__icononly: {
        width: 34,
        padding: 0,
        "@media (max-width:640px)": {
            "&&": {order: 8},
        },

        [COARSE]: {width: 44, padding: 0},
    },
    iconbtn__active: {
        borderColor: theme.modern.accentLine,
        background: theme.modern.accentSoft,
        color: theme.modern.accent,

        "&:hover": {color: theme.modern.accent},
    },

    // language pills
    lang: {
        display: "inline-flex",
        alignItems: "center",
        gap: "1px",
        whiteSpace: "nowrap",
        "@media (max-width:640px)": {
            "&&": {order: 9},
        },
    },
    langbtn: {
        font: `600 11px ${theme.modern.mono}`,
        color: theme.modern.ink3,
        background: "none",
        border: "none",
        padding: "3px 7px",
        borderRadius: 5,
        letterSpacing: ".03em",
        cursor: "pointer",

        "&:hover": {
            color: theme.modern.ink,
            background: theme.modern.line2,
        },

        // the smallest control in the skin at roughly 19 x 26 px. Grown rather
        // than given an oversized ::after hit box, because these sit shoulder to
        // shoulder and overlapping invisible targets would make the language
        // next to the one being aimed at win the tap.
        [COARSE]: touchTarget({padding: "0 12px"}),
    },
    langbtn__active: {
        color: theme.modern.accent,
        background: theme.modern.accentSoft,
    },

    // user block
    user: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        paddingLeft: 12,
        borderLeft: `1px solid ${theme.modern.line}`,
        whiteSpace: "nowrap",
        "@media (max-width:640px)": {
            "&&": {
                order: 2,
                paddingLeft: 0,
                borderLeft: "none",
            },
        },
    },
    avatar: {
        width: 26,
        height: 26,
        borderRadius: "50%",
        background: theme.modern.accent,
        color: theme.modern.onAccent,
        display: "grid",
        placeItems: "center",
        font: `600 12px ${theme.modern.sans}`,
        flex: "none",
    },
    who: {
        lineHeight: 1.15,

        "& b": {display: "block", fontSize: 12, color: theme.modern.ink},
        "& span": {fontSize: 10.5, color: theme.modern.ink3},

        "@media (max-width:1280px)": {"& span": {display: "none"}},
        "@media (max-width:900px)": {display: "none"},
    },
}))
